/**
 * useTenantDashboardData — Sprint 3.9
 *
 * One hook per legacy /tenantdashboard/* POST endpoint:
 *   - useTenantDetail        GET /api/v3/tenants/:id      (tenant profile)
 *   - useTenantPeriodTotals  POST /tenantdashboard/getdetails (yesterday / WTD / MTD / YTD / LY-YTD)
 *   - useTenantCustomTotals  POST /tenantdashboard/gettotals  (custom date range)
 *   - useTenantSalesComparison POST /tenantdashboard/getsalescomparison (YoY chart)
 *   - useTenantSalesMoM      POST /tenantdashboard/getsalescomparisonmom (MoM table)
 *   - useTenantGrowthAnalysis POST /tenantdashboard/getgrowthanalysis (YoY growth table)
 *
 * All hooks share the same shape: { data, loading, error, refetch }.
 * Each fires only when tenantId is truthy; debounce + AbortController borrowed
 * from useWidgetData so rapid filter changes coalesce.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { v3Api } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';

const DEBOUNCE_MS = 250;

function useTenantApi(apiCall, payload, { paused = false, deps = [] } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refetchTick, setRefetchTick] = useState(0);
    const payloadRef = useRef(payload);
    payloadRef.current = payload;

    useEffect(() => {
        if (paused) {
            setLoading(false);
            return;
        }
        const ac = new AbortController();
        const t = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await apiCall(payloadRef.current, { signal: ac.signal });
                if (!ac.signal.aborted) setData(res);
            } catch (err) {
                if (ac.signal.aborted) return;
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                setError(err.response?.data?.message || err.message || 'Request failed');
            } finally {
                if (!ac.signal.aborted) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(t);
            ac.abort();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, paused, refetchTick]);

    return {
        data,
        loading,
        error,
        refetch: () => setRefetchTick(t => t + 1),
    };
}

function commaProps(propertyIds) {
    if (!propertyIds || !propertyIds.length) return undefined;
    return propertyIds.join(',');
}

export function useTenantDetail(tenantId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refetchTick, setRefetchTick] = useState(0);

    useEffect(() => {
        if (!tenantId) {
            setData(null);
            setLoading(false);
            setError(null);
            return;
        }
        const ac = new AbortController();
        setLoading(true);
        setError(null);
        v3Api.tenants.detail(tenantId, { signal: ac.signal })
            .then(res => { if (!ac.signal.aborted) setData(res); })
            .catch(err => {
                if (ac.signal.aborted) return;
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                setError(err.response?.data?.message || err.message || 'Tenant lookup failed');
            })
            .finally(() => { if (!ac.signal.aborted) setLoading(false); });
        return () => ac.abort();
    }, [tenantId, refetchTick]);

    return { data, loading, error, refetch: () => setRefetchTick(t => t + 1) };
}

export function useTenantLifetimeTotals(tenantId) {
    const payload = useMemo(() => ({
        tenant_id: tenantId,
    }), [tenantId]);

    return useTenantApi(
        v3Api.tenantDashboard.lifetimeTotals,
        payload,
        {
            paused: !tenantId,
            deps: [tenantId],
        }
    );
}

export function useTenantPeriodTotals(tenantId, propertyIds) {
    const { concessionaireId } = useAuth();
    const payload = useMemo(() => ({
        concessionaire_id: concessionaireId,
        tenant_id: tenantId,
        property_id: commaProps(propertyIds),
    }), [concessionaireId, tenantId, propertyIds]);

    return useTenantApi(
        v3Api.tenantDashboard.details,
        payload,
        {
            paused: !tenantId || !concessionaireId,
            deps: [tenantId, concessionaireId, commaProps(propertyIds) || ''],
        }
    );
}

export function useTenantCustomTotals(tenantId, propertyIds, dateRange) {
    const { concessionaireId } = useAuth();
    const start_date = dateRange?.from || '';
    const end_date = dateRange?.to || '';
    const payload = useMemo(() => ({
        concessionaire_id: concessionaireId,
        tenant_id: tenantId,
        property_id: commaProps(propertyIds),
        start_date,
        end_date,
    }), [concessionaireId, tenantId, propertyIds, start_date, end_date]);

    return useTenantApi(
        v3Api.tenantDashboard.totals,
        payload,
        {
            paused: !tenantId || !concessionaireId,
            deps: [tenantId, concessionaireId, commaProps(propertyIds) || '', start_date, end_date],
        }
    );
}

export function useTenantSalesComparison(tenantId, propertyIds) {
    const { concessionaireId } = useAuth();
    const payload = useMemo(() => ({
        concessionaire_id: concessionaireId,
        tenant_id: tenantId,
        property_id: commaProps(propertyIds),
    }), [concessionaireId, tenantId, propertyIds]);

    return useTenantApi(
        v3Api.tenantDashboard.salesComparison,
        payload,
        {
            paused: !tenantId || !concessionaireId,
            deps: [tenantId, concessionaireId, commaProps(propertyIds) || ''],
        }
    );
}

export function useTenantSalesMoM(tenantId, propertyIds) {
    const { concessionaireId } = useAuth();
    const payload = useMemo(() => ({
        concessionaire_id: concessionaireId,
        tenant_id: tenantId,
        property_id: commaProps(propertyIds),
    }), [concessionaireId, tenantId, propertyIds]);

    return useTenantApi(
        v3Api.tenantDashboard.salesMoM,
        payload,
        {
            paused: !tenantId || !concessionaireId,
            deps: [tenantId, concessionaireId, commaProps(propertyIds) || ''],
        }
    );
}

export function useTenantGrowthAnalysis(tenantId, year) {
    const { concessionaireId } = useAuth();
    const payload = useMemo(() => ({
        concessionaire_id: concessionaireId,
        tenant_id: tenantId,
        year: year || '',
    }), [concessionaireId, tenantId, year]);

    return useTenantApi(
        v3Api.tenantDashboard.growthAnalysis,
        payload,
        {
            paused: !tenantId || !concessionaireId,
            deps: [tenantId, concessionaireId, year || ''],
        }
    );
}
